import { useEffect, useState } from "react";
import { cookieManager, CookieCategory } from "~/lib/cookie-manager";

interface GoogleAnalyticsProps {
  measurementId?: string;
}

// Google Analytics gtag types with Consent Mode v2 support
type GtagCommand = "config" | "event" | "js" | "set" | "consent";
type GtagConfigParams = {
  send_page_view?: boolean;
  custom_map?: Record<string, string>;
  [key: string]: unknown;
};
type GtagEventParams = {
  event_category?: string;
  event_label?: string;
  business_type?: string;
  location?: string;
  facility_type?: string;
  [key: string]: unknown;
};

// Consent Mode v2 types
type ConsentState = "granted" | "denied";
type ConsentParams = {
  analytics_storage?: ConsentState;
  ad_storage?: ConsentState;
  ad_user_data?: ConsentState;
  ad_personalization?: ConsentState;
  functionality_storage?: ConsentState;
  security_storage?: ConsentState;
  personalization_storage?: ConsentState;
  wait_for_update?: number;
};

declare global {
  interface Window {
    gtag: (
      command: GtagCommand,
      targetId: string | Date | "default" | "update",
      params?: GtagConfigParams | GtagEventParams | ConsentParams,
    ) => void;
    dataLayer: unknown[];
  }
}

export function GoogleAnalytics({ measurementId }: GoogleAnalyticsProps) {
  const GA_MEASUREMENT_ID = measurementId || import.meta.env.VITE_GA_MEASUREMENT_ID;
  const [consentGiven, setConsentGiven] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Initialize Google Analytics with Consent Mode v2 (before any scripts load)
  useEffect(() => {
    if (!GA_MEASUREMENT_ID) {
      console.warn(
        "Google Analytics Measurement ID not found. Please set VITE_GA_MEASUREMENT_ID in your environment variables.",
      );
      return;
    }

    // Initialize dataLayer and gtag before any consent checks
    window.dataLayer = window.dataLayer || [];

    // Define gtag function
    window.gtag = function gtag(
      ...args: [GtagCommand, string | Date | "default" | "update", (GtagConfigParams | GtagEventParams | ConsentParams)?]
    ) {
      window.dataLayer.push(args);
    };

    // Initialize Consent Mode v2 with default denied state
    window.gtag("consent", "default", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      functionality_storage: "granted", // Always granted for essential functionality
      security_storage: "granted", // Always granted for security
      personalization_storage: "granted", // For user preferences (localStorage)
      wait_for_update: 500, // Wait 500ms for consent update
    });

    // Load the GA script immediately (consent mode will control data collection)
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector(
        `script[src*="${GA_MEASUREMENT_ID}"]`,
      );
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [GA_MEASUREMENT_ID]);

  // Check for consent on component mount and when consent changes
  useEffect(() => {
    const checkConsent = () => {
      if (cookieManager.hasConsentBeenSet()) {
        const analyticsConsent = cookieManager.isCategoryConsented(CookieCategory.ANALYTICS);
        const marketingConsent = cookieManager.isCategoryConsented(CookieCategory.MARKETING);

        setConsentGiven(() => analyticsConsent);
        setConsentChecked(() => true);

        // Update consent mode based on user preferences
        if (window.gtag) {
          window.gtag("consent", "update", {
            analytics_storage: analyticsConsent ? "granted" : "denied",
            ad_storage: marketingConsent ? "granted" : "denied",
            ad_user_data: marketingConsent ? "granted" : "denied",
            ad_personalization: marketingConsent ? "granted" : "denied",
          });
        }
      } else {
        // If no consent has been set yet, wait for user decision
        setConsentChecked(() => true);
      }
    };

    checkConsent();

    // Listen for custom events from the cookie banner
    const handleConsentChange = () => {
      checkConsent();
    };

    window.addEventListener("cookieConsentChanged", handleConsentChange);

    return () => {
      window.removeEventListener("cookieConsentChanged", handleConsentChange);
    };
  }, []);

  // Configure Google Analytics when script loads and consent is available
  useEffect(() => {
    if (!GA_MEASUREMENT_ID || !scriptLoaded || !consentChecked) {
      return;
    }

    // Configure Google Analytics (this will respect consent mode settings)
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, {
      // Enhanced ecommerce and local business tracking
      send_page_view: true,
      custom_map: {
        custom_parameter_1: "business_type",
        custom_parameter_2: "location",
      },
      // Respect consent mode - GA will automatically handle data collection based on consent
      anonymize_ip: true, // Additional privacy protection
      allow_google_signals: consentGiven, // Only allow enhanced features if consent given
    });

    // Only track initial page view if analytics consent is given
    if (consentGiven) {
      window.gtag("event", "page_view", {
        business_type: "restaurant_sports_facility",
        location: "morbegno_valtellina",
        custom_parameter_1: "restaurant_sports_facility",
        custom_parameter_2: "morbegno_valtellina",
      });
    }
  }, [GA_MEASUREMENT_ID, scriptLoaded, consentChecked, consentGiven]);

  return null;
}

// Helper function to update consent mode
export const updateGoogleAnalyticsConsent = (analyticsConsent: boolean, marketingConsent: boolean) => {
  if (typeof window !== "undefined" && window.gtag) {
    try {
      window.gtag("consent", "update", {
        analytics_storage: analyticsConsent ? "granted" : "denied",
        ad_storage: marketingConsent ? "granted" : "denied",
        ad_user_data: marketingConsent ? "granted" : "denied",
        ad_personalization: marketingConsent ? "granted" : "denied",
      });
    } catch (error) {
      console.warn('Failed to update Google Analytics consent mode:', error);
    }
  }
};

// Enhanced analytics tracking for Tennis Gourmet business insights
export const trackLocalBusinessEvent = (
  eventName: string,
  parameters?: Record<string, unknown>,
) => {
  // With Consent Mode v2, we can always call gtag - it will respect consent settings
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, {
      business_type: "restaurant_sports_facility",
      location: "morbegno_valtellina",
      business_category: "hospitality_sports",
      ...parameters,
    });
  }
};

// Track user engagement with different sections
export const trackSectionView = (section: string, timeSpent?: number) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "section_view", {
      section_name: section,
      engagement_time: timeSpent,
      event_category: "user_engagement"
    });
  }
};

// Track menu interactions
export const trackMenuInteraction = (menuItem: string, action: 'view' | 'click' | 'hover') => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "menu_interaction", {
      menu_item: menuItem,
      interaction_type: action,
      event_category: "menu_engagement"
    });
  }
};

// Track contact form interactions
export const trackContactFormEvent = (action: 'start' | 'complete' | 'abandon', formData?: Record<string, unknown>) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "contact_form", {
      form_action: action,
      event_category: "lead_generation",
      ...formData
    });
  }
};

// Track reservation/booking interest
export const trackReservationInterest = (type: 'restaurant' | 'sports', method: 'phone' | 'email' | 'form') => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "reservation_interest", {
      reservation_type: type,
      contact_method: method,
      event_category: "conversion_intent",
      value: type === 'restaurant' ? 50 : 30 // Estimated value
    });
  }
};

// Track social media clicks
export const trackSocialClick = (platform: string, location: 'header' | 'footer' | 'contact') => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "social_click", {
      social_platform: platform,
      click_location: location,
      event_category: "social_engagement"
    });
  }
};

// Track image gallery interactions
export const trackGalleryInteraction = (action: 'open' | 'navigate' | 'close', imageIndex?: number) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "gallery_interaction", {
      gallery_action: action,
      image_index: imageIndex,
      event_category: "media_engagement"
    });
  }
};

// Track scroll depth for engagement measurement
export const trackScrollDepth = (percentage: number, page: string) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "scroll_depth", {
      scroll_percentage: percentage,
      page_location: page,
      event_category: "user_engagement"
    });
  }
};

// Track device and performance insights
export const trackPerformanceMetrics = (metrics: {
  pageLoadTime?: number;
  deviceType?: string;
  connectionSpeed?: string;
}) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "performance_metrics", {
      page_load_time: metrics.pageLoadTime,
      device_type: metrics.deviceType,
      connection_speed: metrics.connectionSpeed,
      event_category: "technical_insights"
    });
  }
};

export const trackRestaurantReservation = () => {
  trackLocalBusinessEvent("restaurant_reservation_attempt", {
    event_category: "restaurant",
    event_label: "reservation_form",
  });
};

export const trackSportsBooking = (facility: string) => {
  trackLocalBusinessEvent("sports_booking_attempt", {
    event_category: "sports",
    event_label: facility,
    facility_type: facility,
  });
};

export const trackMenuView = () => {
  trackLocalBusinessEvent("menu_view", {
    event_category: "restaurant",
    event_label: "menu_pdf",
  });
};

export const trackContactFormSubmission = () => {
  trackLocalBusinessEvent("contact_form_submission", {
    event_category: "contact",
    event_label: "contact_form",
  });
};
